import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useProfiles, type Profile } from '@/api/profiles'
import { RefreshCcw, Loader2 } from 'lucide-react'
import Card from '@/components/Card'
import SectionHead from '@/components/SectionHead'
import Dot from '@/components/Dot'
import ProfileSyncGrid, { type ProfileSyncEntry } from '@/components/ProfileSyncGrid'
import SyncRunHistoryTable, {
  type SyncHistoryFilter,
  type SyncHistoryRow,
} from '@/components/SyncRunHistoryTable'
import type { BadgeTone } from '@/components/StatusBadge'
import { cn } from '@/lib/utils'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import SyncTypeSelect from '@/components/SyncTypeSelect'
import { SYNC_TYP_DEFAULT, type SyncTyp } from '@/lib/syncTypes'

interface SyncRun {
  id: number
  profileId: number
  syncType: string
  status: 'running' | 'completed' | 'failed'
  entitiesSynced: number | null
  errors: string[] | null
  startedAt: string
  completedAt: string | null
}

const SYNC_TYPE_LABEL: Record<string, string> = {
  full: 'Full Sync',
  entities: 'Entity-Sync',
  metrics: 'Daily Metrics',
  search_terms: 'Search Terms',
  backfill: '60-Tage-Backfill',
}

/**
 * Wie viele Laeufe die Historie holt.
 *
 * Die Route deckelt auf 100 (`backend/src/modules/sync/routes.ts:51`,
 * `Math.min(parseInt(query.limit ?? '20', 10) || 20, 100)`). Die Seite forderte
 * 200 und bekam still 100 — die Zahl im Aufruf log also, und niemand haette es
 * bemerkt, weil eine gedeckelte Antwort genauso aussieht wie eine kurze.
 * Diese Konstante fordert genau das, was die Route liefern kann; wer den Deckel
 * hebt, muss BEIDE Stellen anfassen (das Duplikat ist als Registerpunkt
 * SYNC-TYPLISTE-DUPLIZIERT-VERTRAG/-LIMIT vermerkt).
 */
const SYNC_RUNS_LIMIT = 100

/**
 * Wie lange auf einen einzelnen HISTORIEN-Abruf gewartet wird.
 *
 * Ein `fetch` ohne Abbruch kann sehr lange offen bleiben. Wo eine SCHLEIFE an
 * so einem Abruf haengt, haengt sie unbegrenzt: Im Sammellauf loesen sich
 * `bulkSyncing` und die Zeilensperre der ersten Stufe in einem `finally`, das
 * dann nie erreicht wird — auch ein gelungener Abruf von aussen kommt dagegen
 * nicht an (R1-Befund, fuenfte Runde). Und im Takt legt sich ohne Frist alle
 * fuenf Sekunden ein weiterer offener Abruf oben drauf (R1-Befund, sechste
 * Runde).
 */
const ABRUF_FRIST_MS = 15_000

/**
 * Und wie lange auf die Antwort einer START-Anfrage gewartet wird. Grosszuegiger
 * als beim Lesen: Ein Start legt einen Lauf an, das darf laenger dauern.
 *
 * Ohne diese Frist sperrte eine Start-Anfrage, die nie beantwortet wird, die
 * Zeile DAUERHAFT (R1-Befund, sechste Runde): Die erste Sperrstufe loest sich
 * nur im `finally` des Start-Pfades, und an dieses `finally` kommt niemand mehr
 * heran — kein Takt, kein Abruf von aussen, kein zweiter Bedienweg.
 */
const START_FRIST_MS = 30_000

/**
 * Wie lange ein ANFRAGE-PLATZ hoechstens belegt bleibt, wenn die Anfrage nicht
 * endet (R1+R2 unabhaengig, neunte Runde).
 *
 * Takt und Warteschleife halten hoechstens eine offene Anfrage. Der Platz wird
 * frei, sobald sie wirklich endet — nur endet sie manchmal nie: eine
 * abgerissene Verbindung ohne Fehlermeldung laesst das Versprechen fuer immer
 * offen. Damit stand die Seite dauerhaft still, obwohl das Backend laengst
 * wieder antwortete — und `ladeFehler`, der sich laut Zusicherung dieser
 * Scheibe SELBST aufloest, blieb bis zum naechsten Klick stehen.
 *
 * Nach dieser Zeit gilt die Anfrage als verloren — und VERFALL = ABBRUCH
 * (Scheibe SYNC-ABBRUCH-UND-STARTZEICHEN): Der Platz wird frei UND die Anfrage
 * wird per `AbortController` beendet. Vorher lief sie weiter und belegte eine
 * Verbindung des Browsers; eine spaete Antwort nach dem Verfall gibt es
 * seitdem nicht mehr — die neue Anfrage fragt frisch. Alle LESE-Anfragen sind
 * ausserdem an die gemountete Seiteninstanz gebunden und werden beim Unmount
 * abgebrochen; die 60-Sekunden-Verfallsfrist gilt weiterhin nur fuer Takt und
 * Warteschleife. Eine START-Anfrage wird nie abgebrochen (siehe Registerpunkt
 * SYNC-START-OHNE-ABBRUCH): ein abgebrochener POST kann laengst einen Lauf
 * angelegt haben.
 */
const PLATZ_VERFALL_MS = 60_000

/**
 * Wie lange die Seite dem Backend Zeit laesst, einen gerade angelegten Lauf
 * auch wirklich zu schreiben, bevor sie die Historie danach fragt.
 *
 * Der Erfolgsweg hat diesen Vorlauf von Anfang an. Der FEHLERWEG braucht ihn
 * genauso (R1+R2, elfte Runde): Er fragt gerade in dem Fall nach, in dem der
 * Lauf trotz Fehler entstanden sein kann — eine Antwort "kein Lauf", die nur
 * zu frueh gelesen wurde, gibt die Zeile frei, waehrend der Sync anlaeuft.
 * Der Vorlauf macht daraus keine Zusicherung: Er verkleinert das Fenster,
 * schliesst es aber nicht (siehe Registerpunkt SYNC-TRANSPORT-OHNE-REIHENFOLGE).
 */
const SCHREIB_VORLAUF_MS = 1_000

/**
 * Marke fuer „die Frist lief ab, bevor eine Antwort kam".
 *
 * Ein eigener Wert und nicht `null` (R1-Befund, sechste Runde): Eine Antwort
 * darf selbst leer sein, und dann muss der Unterschied zwischen „nichts
 * geantwortet" und „nichts gekommen" noch lesbar sein.
 */
const OHNE_ANTWORT = Symbol('ohne-antwort')
/** Eine Lebenszyklus-Grenze hat ein Warten beendet, nicht dessen Fachfrist. */
const LEBENSZYKLUS_ENDE = Symbol('lebenszyklus-ende')

/**
 * Erkennt den Abbruch, den die Seite SELBST ausgeloest hat (`AbortController`).
 * Er ist kein Zustand der Historie — weder Fehler noch Erfolg — und darf
 * deshalb weder den roten Ladehinweis setzen noch als juengstes Urteil zaehlen.
 */
function istAbbruch(fehler: unknown): boolean {
  return typeof fehler === 'object' && fehler !== null && (fehler as { name?: unknown }).name === 'AbortError'
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'noch nie'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'gerade'
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'gerade'
  if (min < 60) return `vor ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `vor ${h} h`
  const d = Math.floor(h / 24)
  return `vor ${d} d`
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return '–'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`
  const totalSec = Math.round(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}m ${sec}s`
}

export default function Sync() {
  const queryClient = useQueryClient()
  const { data: profilesData } = useProfiles()
  const profiles = profilesData ?? []
  const [runs, setRuns] = useState<SyncRun[]>([])
  const [filter, setFilter] = useState<SyncHistoryFilter>('all')
  // EINE MENGE, kein einzelner Wert: Mit einem einzelnen `syncingId` hob der
  // Start eines zweiten Profils die Sperre des ersten auf, und das `finally`
  // der einen Anfrage entsperrte die andere mit. Zwei Klicks kurz
  // hintereinander konnten denselben Sync doppelt starten — genau die
  // Zombie-Runs, vor denen der Hinweis an `waitForRunDone` warnt.
  const [startendeProfile, setStartendeProfile] = useState<ReadonlySet<number>>(new Set())

  /** Merkt sich, dass fuer dieses Profil gerade eine Start-Anfrage laeuft. */
  function merkeStart(profileId: number, generation: number) {
    if (!generationIstAktiv(generation)) return
    setStartendeProfile((vorher) => new Set(vorher).add(profileId))
  }

  /** Nimmt NUR dieses Profil heraus — fremde Sperren bleiben unberuehrt. */
  function vergissStart(profileId: number, generation: number) {
    if (!generationIstAktiv(generation)) return
    setStartendeProfile((vorher) => {
      const naechste = new Set(vorher)
      naechste.delete(profileId)
      return naechste
    })
  }

  /**
   * DIE ZWEITE SPERRSTUFE: Profile, deren Start DURCHGING, deren Lauf aber noch
   * in keiner angekommenen Historie steht (R1+R2 unabhaengig, vierte Runde).
   *
   * Nach dem Entsperren haengt der Schutz vor dem Doppelstart allein daran, dass
   * der neue Lauf in `runs` steht — `laufendeProfile` liest ihn aus der Tabelle,
   * nicht aus dem Klick. `loadRuns` wirft seinen Fehler aber nicht weiter,
   * sondern zeigt ihn als Zustand an: Scheiterte das Nachladen nach einem
   * gelungenen Start, lief der Start-Pfad ganz normal in sein `finally`,
   * entsperrte die Zeile — und die Tabelle wusste nichts von dem Lauf, der
   * gerade begonnen hatte. Der naechste Klick startete denselben Sync erneut.
   *
   * Die Gegenrichtung ist genauso schlimm und war in derselben Runde der zweite
   * Teil des Befundes: Haengt das Nachladen fuer immer, wird das `finally` NIE
   * erreicht — an der ersten Stufe allein bliebe die Zeile dauerhaft gesperrt,
   * auch lange nachdem der Lauf fertig ist. Deshalb wandert die Sperre, sobald
   * die Start-Antwort da ist, von "Anfrage offen" in diese zweite Stufe: Sie
   * loest sich nicht am eigenen `finally`, sondern an der naechsten
   * angekommenen Liste — egal, wer sie geholt hat.
   *
   * Der Wert je Profil ist die SCHWELLE: die kleinste Abrufnummer, die den Lauf
   * enthalten MUSS. Ein Abruf, der schon vor der Start-Antwort unterwegs war,
   * kann ihn noch nicht kennen; wuerde er die Sperre loesen, waere genau das
   * Fenster wieder offen, das sie schliessen soll.
   */
  const [unbestaetigteStarts, setUnbestaetigteStarts] = useState<ReadonlyMap<number, number>>(
    new Map(),
  )

  /**
   * Ab jetzt gilt: erst eine Liste, die NACH der Start-Antwort losgeschickt
   * wurde, darf dieses Profil wieder freigeben.
   */
  function merkeUnbestaetigt(profileId: number, generation: number, ohneAntwort = false) {
    if (!generationIstAktiv(generation)) return
    // JETZT ablesen, nicht im Aktualisierer (R1-Befund, fuenfte Runde): React
    // darf die Funktion unten spaeter ausfuehren oder wiederholen. Bis dahin
    // koennen weitere Abrufe den Zaehler erhoeht haben — die Schwelle laege
    // dann ueber Abrufen, die den Lauf laengst kennen, und die Zeile bliebe
    // gesperrt, obwohl die Bestaetigung da war.
    // OHNE ANTWORT GIBT ES KEINE SCHWELLE (R1+R2 unabhaengig, siebte Runde):
    // Solange die Start-Anfrage laeuft, kann KEINE Liste den Lauf kennen — auch
    // keine, die spaeter losgeschickt wird. Eine gewoehnliche Schwelle liesse
    // die naechste angekommene Liste die Zeile freigeben, waehrend der Start
    // noch unterwegs ist; legt er danach doch einen Lauf an, ist die Zeile
    // bedienbar, waehrend der Sync laeuft. `schwelleNachAntwort` ersetzt diesen
    // Wert, sobald die Anfrage wirklich beantwortet ist.
    const schwelle = ohneAntwort ? Number.POSITIVE_INFINITY : abrufZaehler.current + 1
    setUnbestaetigteStarts((vorher) => new Map(vorher).set(profileId, schwelle))
  }

  /**
   * Haengt die SCHWELLE an die wirkliche Antwort der Start-Anfrage — nicht an
   * den Moment, in dem die Seite aufgehoert hat, auf sie zu warten (R1+R2
   * unabhaengig, siebte Runde).
   *
   * Eine Frist beendet nur das Warten, nicht die Anfrage. Nach ihrem Ablauf ist
   * der Start weiter unterwegs und kann jederzeit noch einen Lauf anlegen. Eine
   * Liste, die in dieser Zeit losgeschickt wurde, kann ihn unmoeglich kennen —
   * sie loeste die Sperre trotzdem, weil ihre Nummer ueber der Schwelle lag.
   * Danach war die Zeile bedienbar, waehrend der Sync anlief: genau der
   * Doppelstart, gegen den die zweite Stufe gebaut ist.
   *
   * EINE ABLEHNUNG BEWEIST KEINEN NICHT-LAUF (R1-Befund, neunte Runde): Bricht
   * die Verbindung erst, NACHDEM das Backend den Lauf angelegt hat, sieht die
   * Seite denselben Fehler wie bei einer Anfrage, die den Server nie erreicht
   * hat. Die Sperre deshalb sofort wegzunehmen, gab die Zeile fuer einen
   * zweiten Start frei, waehrend der erste Lauf schon lief.
   *
   * Beide Ausgaenge klaeren die Anfrage darum gleich: Ab jetzt darf die naechste
   * Liste entscheiden — sie ging nach dem Ende der Anfrage los und kennt den
   * Lauf, wenn es ihn gibt. Die Zeile bleibt also hoechstens bis zur naechsten
   * Liste gesperrt (der Takt laeuft, solange ein Start unbestaetigt ist), statt
   * auf eine Vermutung hin zu oeffnen.
   */
  function schwelleNachAntwort(profileId: number, anfrage: Promise<unknown>, generation: number) {
    const klaeren = () => merkeUnbestaetigt(profileId, generation)
    void anfrage.then(klaeren, klaeren)
  }
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [bulkSyncing, setBulkSyncing] = useState(false)
  // Der Typ des SAMMELLAUFS — einer fuer alle Profile dieses Laufs.
  const [bulkTyp, setBulkTyp] = useState<SyncTyp>(SYNC_TYP_DEFAULT)
  // Der Typ JE PROFILZEILE. Fehlt ein Eintrag, gilt der Vorgabewert; damit
  // verhaelt sich ein unberuehrter Klick genau wie vor dieser Aenderung.
  const [syncTypen, setSyncTypen] = useState<Record<number, SyncTyp>>({})
  const [refreshing, setRefreshing] = useState(false)
  // ZWEI PLAETZE, kein gemeinsamer. Die Seite meldet zwei grundverschiedene
  // Dinge, und solange sie sich einen Platz teilten, loeschte jedes das andere:
  //
  // - `meldung` ist ein EREIGNIS ("dein Sammellauf hat Profil X nicht
  //   geschafft"). Es ist einmalig, niemand wiederholt es, und es gilt weiter,
  //   auch wenn die Historie zwischendurch hakt. Es verschwindet erst, wenn der
  //   Bediener es wegklickt oder selbst eine neue Aktion beginnt.
  // - `ladeFehler` ist ein ZUSTAND ("die Historie ist gerade nicht ladbar").
  //   Er heilt sich selbst: der naechste gelungene Abruf nimmt ihn zurueck.
  //
  // Mit einem gemeinsamen Platz — auch mit Herkunftsschild — verschwand das
  // Ereignis endgueltig: Ein Netz-Ruckler ueberschrieb die Sammellauf-Meldung,
  // der Takt darauf gelang und raeumte den Ladefehler weg. Uebrig blieb nichts,
  // und ein teilweise gescheiterter Sammellauf sah am Ende aus wie ein
  // gelungener (R1+R2 unabhaengig, erste Runde). Umgekehrt genauso: eine
  // beliebige Aktion loeschte einen Ladefehler, der weiterhin galt.
  const [meldung, setMeldung] = useState<string | null>(null)
  const [ladeFehler, setLadeFehler] = useState<string | null>(null)

  /**
   * Laufende Nummer der Historien-Abrufe (`abrufZaehler` vergibt sie,
   * `letzteAngewandte` merkt sich, welche zuletzt geschrieben hat).
   *
   * Die Seite ruft die Historie von sechs Stellen ab: Mount, Takt alle fuenf
   * Sekunden, Nachladen nach dem Einzelstart, je Profil im Sammellauf,
   * Nachlauf des Sammellaufs, Knopf. Sie ueberholen einander (R1-Befund).
   * Ohne Nummer entschied die Reihenfolge der ANTWORTEN: ein alter,
   * gescheiterter Abruf setzte den Ladefehler wieder, nachdem ein neuerer
   * laengst erfolgreich war — das rote Banner stand dann ueber einer aktuellen
   * Tabelle. Umgekehrt genauso: eine alte, gelungene Antwort loeschte den
   * Fehler eines neueren Abrufs und gab eine Entwarnung, die nicht galt.
   *
   * DIE REGEL LAUTET NICHT "nur der juengste darf schreiben", sondern:
   * SCHREIBEN, SOLANGE NICHTS JUENGERES SCHON GESCHRIEBEN HAT. Der Unterschied
   * ist nicht akademisch (R1-Befund der dritten Runde, an meiner eigenen ersten
   * Fassung): Der Einzelstart wartet auf sein Nachladen und gibt die Zeile
   * ERST DANACH frei. Verwarf man sein Ergebnis, bloss weil inzwischen ein
   * Takt-Abruf unterwegs war, und dieser Takt scheiterte dann oder blieb
   * haengen, wurde die Zeile freigegeben, ohne dass der neue Lauf je in der
   * Tabelle stand — der Bediener konnte denselben Sync ein zweites Mal
   * starten. Genau diese Sperre hatte eine fruehere Scheibe gebaut.
   *
   * UND ES SIND ZWEI GETRENNTE FRAGEN, nicht eine:
   * - `letzteDaten` — wessen LISTE steht in der Tabelle. Nur ein gelungener
   *   Abruf bringt Daten mit; ein gescheiterter hat dazu nichts zu sagen.
   * - `letzterZustand` — was zuletzt ueber die ERREICHBARKEIT bekannt wurde.
   *   Dazu sagt jeder Abruf etwas, auch der gescheiterte.
   * Mit einem einzigen Zaehler verschluckte ein gescheiterter Takt-Abruf die
   * Daten eines aelteren, gelungenen — genau der Fall aus dem Start-Pfad oben.
   * Getrennt gefuehrt steht die Tabelle auf dem besten vorhandenen Stand, und
   * das rote Banner sagt trotzdem die Wahrheit: der letzte Versuch ging schief.
   */
  const abrufZaehler = useRef(0)
  const letzteDaten = useRef(0)
  const letzterZustand = useRef(0)
  /**
   * Der Platz des Takts — KOMPONENTENWEIT, nicht im Effect (R1-Befund, neunte
   * Runde). Als Variable im Effect fiel er bei jedem Neuaufbau zurueck: Ein
   * Abruf von aussen, der neue Daten bringt, baut den Effect neu auf, und der
   * naechste Takt schickte trotz offener Anfrage los. Der Merker haelt den
   * Platz ueber solche Neuaufbauten hinweg.
   */
  const taktPlatz = useRef<Promise<unknown> | null>(null)

  /**
   * Alle asynchronen Seitenketten gehoeren zu GENAU einer Mount-Generation.
   *
   * Ein blosses `mounted: boolean` reicht unter React StrictMode nicht: Dessen
   * Probe fuehrt Effect-Setup, Cleanup und Setup auf derselben Instanz aus. Eine
   * alte, nach dem zweiten Setup eintreffende Antwort saehe dann wieder
   * `mounted=true` und koennte in die neue Generation schreiben. Die Nummer
   * trennt beide Generationen, ohne module-globalen Zustand.
   */
  const lebenszyklusGeneration = useRef(0)
  const istGemountet = useRef(false)
  const leseSteuerungen = useRef(new Set<AbortController>())
  type Zeitgeber = ReturnType<typeof setTimeout>
  const lebenszyklusZeitgeber = useRef(new Map<Zeitgeber, () => void>())

  function generationIstAktiv(generation: number): boolean {
    return istGemountet.current && lebenszyklusGeneration.current === generation
  }

  function registriereLeseSteuerung(steuerung: AbortController, generation: number): boolean {
    if (!generationIstAktiv(generation)) {
      steuerung.abort()
      return false
    }
    leseSteuerungen.current.add(steuerung)
    return true
  }

  function vergissLeseSteuerung(steuerung: AbortController): void {
    leseSteuerungen.current.delete(steuerung)
  }

  function verwerfeZeitgeber(zeitgeber: Zeitgeber | null): void {
    if (zeitgeber === null) return
    clearTimeout(zeitgeber)
    lebenszyklusZeitgeber.current.delete(zeitgeber)
  }

  /**
   * Ein instanzlokaler Timeout. Beim Unmount wird er geloescht und sein
   * Abbruchbehandler aufgerufen; dadurch bleiben keine fuer immer wartenden
   * Promise-Ketten zurueck.
   */
  function planeZeitgeber(
    aufgabe: () => void,
    ms: number,
    generation: number,
    beiUnmount: () => void = () => undefined,
  ): Zeitgeber | null {
    if (!generationIstAktiv(generation)) {
      beiUnmount()
      return null
    }
    let zeitgeber: Zeitgeber
    zeitgeber = setTimeout(() => {
      lebenszyklusZeitgeber.current.delete(zeitgeber)
      if (generationIstAktiv(generation)) aufgabe()
      else beiUnmount()
    }, ms)
    lebenszyklusZeitgeber.current.set(zeitgeber, beiUnmount)
    return zeitgeber
  }

  function warteImLebenszyklus(ms: number, generation: number): Promise<boolean> {
    return new Promise<boolean>((aufloesen) => {
      let fertig = false
      let zeitgeber: Zeitgeber | null = null
      const beenden = (weiter: boolean) => {
        if (fertig) return
        fertig = true
        if (zeitgeber !== null) lebenszyklusZeitgeber.current.delete(zeitgeber)
        aufloesen(weiter)
      }
      zeitgeber = planeZeitgeber(
        () => beenden(true),
        ms,
        generation,
        () => beenden(false),
      )
    })
  }

  /**
   * Die bisherige 15-/30-Sekunden-Fachfrist, jetzt an die Mount-Generation
   * gebunden. Eine Ablehnung bleibt eine Ablehnung; nur ein Unmount liefert die
   * eigene Lebenszyklus-Marke. START-POSTs werden dadurch NICHT abgebrochen:
   * lediglich ihr lokales Warten endet, waehrend das POST-Promise weiterlaufen
   * und seinen unbekannten Serverausgang behalten darf.
   */
  function mitLebenszyklusFrist<T>(
    versprechen: Promise<T>,
    ms: number,
    generation: number,
  ): Promise<T | typeof OHNE_ANTWORT | typeof LEBENSZYKLUS_ENDE> {
    return new Promise((aufloesen, ablehnen) => {
      let fertig = false
      let zeitgeber: Zeitgeber | null = null
      const beenden = (aktion: () => void) => {
        if (fertig) return
        fertig = true
        verwerfeZeitgeber(zeitgeber)
        aktion()
      }
      zeitgeber = planeZeitgeber(
        () => beenden(() => aufloesen(OHNE_ANTWORT)),
        ms,
        generation,
        () => beenden(() => aufloesen(LEBENSZYKLUS_ENDE)),
      )
      void versprechen.then(
        (wert) => beenden(() => aufloesen(wert)),
        (fehler) => beenden(() => ablehnen(fehler)),
      )
    })
  }

  useEffect(() => {
    const generation = ++lebenszyklusGeneration.current
    istGemountet.current = true
    return () => {
      if (lebenszyklusGeneration.current !== generation) return
      istGemountet.current = false
      // Auch eine direkt folgende StrictMode-Setup-Runde darf Antworten dieser
      // Generation nicht wieder als aktuell ansehen.
      lebenszyklusGeneration.current++
      taktPlatz.current = null

      const steuerungen = [...leseSteuerungen.current]
      leseSteuerungen.current.clear()
      for (const steuerung of steuerungen) steuerung.abort()

      const zeitgeber = [...lebenszyklusZeitgeber.current.entries()]
      lebenszyklusZeitgeber.current.clear()
      for (const [id, beiUnmount] of zeitgeber) {
        clearTimeout(id)
        beiUnmount()
      }
    }
  }, [])

  const loadRuns = useCallback(async (steuerung = new AbortController()) => {
    const generation = lebenszyklusGeneration.current
    if (!registriereLeseSteuerung(steuerung, generation)) return
    const meiner = ++abrufZaehler.current
    if (generationIstAktiv(generation)) setRefreshing(true)
    try {
      const data = await api.get<{ runs: SyncRun[] }>(`/sync/runs/?limit=${SYNC_RUNS_LIMIT}`, {
        signal: steuerung.signal,
      })
      if (!generationIstAktiv(generation)) return
      if (meiner > letzteDaten.current) {
        letzteDaten.current = meiner
        setRuns(data.runs ?? [])
      }
      // Diese Liste ist angekommen: Fuer jedes Profil, dessen Schwelle sie
      // erreicht, ist die Frage "steht der Start drin?" jetzt beantwortet — ob
      // der Lauf drinsteht, entscheidet danach wieder `laufendeProfile`. Auch
      // wenn ein juengerer Abruf die Tabelle schon geschrieben hat: dann steht
      // dort etwas noch Frischeres, das den Lauf erst recht kennen muesste.
      setUnbestaetigteStarts((vorher) => {
        const bleiben = [...vorher].filter(([, schwelle]) => meiner < schwelle)
        return bleiben.length === vorher.size ? vorher : new Map(bleiben)
      })
      // Der Ladefehler ist mit dem gelungenen Abruf erledigt. Blieb er stehen,
      // widersprachen sich zwei Anzeigen derselben Seite: die Tabelle zeigt
      // einen sauber beendeten Lauf, das rote Banner daneben behauptet, die
      // Historie sei nicht ladbar.
      if (meiner > letzterZustand.current) {
        letzterZustand.current = meiner
        setLadeFehler(null)
      }
    } catch (err) {
      // EIN EIGENER ABBRUCH IST KEIN URTEIL (E5): weder Fehler noch Erfolg.
      // `letzterZustand` bleibt, wo er ist — ein aelterer, noch laufender
      // Abruf darf seins noch abgeben; und `finally` raeumt das Ladezeichen.
      if (istAbbruch(err) || !generationIstAktiv(generation)) return
      if (meiner > letzterZustand.current) {
        letzterZustand.current = meiner
        setLadeFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen')
      }
    } finally {
      vergissLeseSteuerung(steuerung)
      // Das Ladezeichen gehoert dem juengsten Abruf: ein ueberholter darf es
      // nicht abschalten, waehrend der aktuelle noch laeuft.
      if (generationIstAktiv(generation) && meiner === abrufZaehler.current) setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    loadRuns()
  }, [loadRuns])

  // Auto-Refresh waehrend ein Sync laeuft — UND solange ein Start unbestaetigt
  // ist (R2-Befund, fuenfte Runde). Der Takt hing bisher allein an einem Lauf,
  // der schon in der Tabelle steht. Genau der fehlt aber, wenn das Nachladen
  // nach dem Start gescheitert ist: Die Zeile war gesperrt, und der einzige Weg
  // heraus war ein Klick des Bedieners auf „Aktualisieren". Jetzt fragt die
  // Seite selbst weiter, bis wieder eine Liste ankommt.
  useEffect(() => {
    const generation = lebenszyklusGeneration.current
    if (!generationIstAktiv(generation)) return
    const hasRunning = runs.some((r) => r.status === 'running')
    if (!hasRunning && unbestaetigteStarts.size === 0) return
    // EINER ZUR ZEIT, GEMESSEN AN DER WIRKLICHEN ANTWORT (R1-Befund sechste
    // Runde, verschaerft durch R1+R2 in der siebten): Antwortet die Historie
    // dauerhaft nicht, legte der Takt alle fuenf Sekunden einen weiteren
    // offenen Abruf oben drauf — und weil der Takt in genau dieser Lage nicht
    // endet, unbegrenzt viele. Eine Frist half dagegen nur scheinbar: Sie
    // beendet das WARTEN, nicht die Anfrage. Wer nach ihrem Ablauf den
    // naechsten Abruf losschickt, stapelt weiter, bloss langsamer.
    //
    // Der Takt schweigt deshalb, solange sein Abruf offen ist. Das kostet
    // nichts: Ein Backend, das nicht antwortet, antwortet auch dem naechsten
    // Abruf nicht. Und der Ausweg bleibt offen — „Aktualisieren" ist nicht
    // gesperrt und darf jederzeit ueberholen.
    //
    // ABER DER PLATZ VERFAELLT (R1+R2 unabhaengig, neunte Runde): Ein
    // Versprechen, das nie endet, gibt ihn sonst nie wieder her — der Takt
    // schwieg danach fuer immer, und die Seite erholte sich selbst dann nicht,
    // wenn das Backend laengst wieder antwortete. Genau das ist der Fall, gegen
    // den (a) gebaut ist: Ein Ladefehler soll sich von allein aufloesen.
    const interval = setInterval(() => {
      if (!generationIstAktiv(generation) || taktPlatz.current) return
      const steuerung = new AbortController()
      const meiner = loadRuns(steuerung)
      taktPlatz.current = meiner
      // NUR DER EIGENE PLATZ wird freigegeben: Ein frueh beantworteter Abruf
      // hat einen Verfall-Timer, der spaeter faellt, wenn laengst der
      // Nachfolger den Platz haelt.
      const freigeben = () => {
        if (taktPlatz.current === meiner) taktPlatz.current = null
      }
      void meiner.catch(() => null).then(() => {
        freigeben()
      })
      // VERFALL = ABBRUCH: die verlorene Anfrage wird beendet, nicht bloss
      // vergessen. `abort()` auf einer laengst beendeten Anfrage ist folgenlos.
      planeZeitgeber(() => {
        steuerung.abort()
        freigeben()
      }, PLATZ_VERFALL_MS, generation)
    }, 5000)
    return () => clearInterval(interval)
  }, [runs, loadRuns, unbestaetigteStarts])

  // Aggregate fuer Status-Cards & Profile-Grid
  const profileMeta = useMemo(() => {
    const map = new Map<
      number,
      {
        lastRun: SyncRun | null
        lastCompleted: SyncRun | null
      }
    >()
    for (const p of profiles) {
      map.set(p.id, { lastRun: null, lastCompleted: null })
    }
    const sorted = [...runs].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    )
    for (const run of sorted) {
      const m = map.get(run.profileId)
      if (!m) continue
      if (!m.lastRun) m.lastRun = run
      if (!m.lastCompleted && run.status === 'completed') m.lastCompleted = run
    }
    return map
  }, [profiles, runs])

  function profileStatus(p: Profile): { tone: BadgeTone; label: string } {
    if (!p.isActive) return { tone: 'neutral', label: 'Inaktiv' }
    const meta = profileMeta.get(p.id)
    if (!meta || (!meta.lastRun && !meta.lastCompleted)) {
      return { tone: 'neutral', label: 'Kein Sync' }
    }
    // Aktiv laufender Sync hat Vorrang
    if (meta.lastRun?.status === 'running') return { tone: 'info', label: 'Läuft' }
    // Letzter erfolgreicher Sync ist Quelle der Wahrheit — ein zwischen-
    // zeitlicher manueller Trigger-Fehler (z.B. parallel-Bulk) ueberschreibt
    // den OK-Status nicht
    if (meta.lastCompleted) {
      const ageMs = Date.now() - new Date(meta.lastCompleted.startedAt).getTime()
      const hasErrors = meta.lastCompleted.errors && meta.lastCompleted.errors.length > 0
      if (ageMs < 6 * 60 * 60_000) {
        return hasErrors ? { tone: 'warn', label: 'Warnung' } : { tone: 'good', label: 'OK' }
      }
      if (ageMs < 30 * 24 * 60 * 60_000) return { tone: 'warn', label: 'Veraltet' }
      return { tone: 'warn', label: 'Veraltet' }
    }
    // Kein Erfolg vorhanden, nur Failure
    if (meta.lastRun?.status === 'failed') return { tone: 'bad', label: 'Fehler' }
    return { tone: 'neutral', label: 'Kein Sync' }
  }

  const profileEntries: ProfileSyncEntry[] = profiles.map((p) => {
    const meta = profileMeta.get(p.id)
    const last = meta?.lastCompleted ?? meta?.lastRun ?? null
    const status = profileStatus(p)
    return {
      id: p.id,
      name: p.name,
      marketplace: p.marketplace,
      statusTone: status.tone,
      statusLabel: status.label,
      lastSyncRelative: formatRelative(last?.startedAt ?? null),
      entitiesLast: meta?.lastCompleted?.entitiesSynced ?? null,
      isActive: p.isActive === 1,
    }
  })

  // Welche Profile haben gerade einen laufenden Sync? Aus den geladenen
  // Laeufen, nicht aus dem Klick: der Start-Request ist beantwortet, lange
  // bevor der Lauf fertig ist (R1-Befund).
  const laufendeProfile = new Set(runs.filter((r) => r.status === 'running').map((r) => r.profileId))

  // BEIDE STUFEN zusammen sperren einen Start: die offene Start-Anfrage und der
  // Start, den noch keine Historie bestaetigt hat. Faellt eine davon weg,
  // entsteht genau dazwischen das Fenster fuer den zweiten Klick. Der
  // Sammellauf-Knopf sieht die Vereinigung; das Grid bekommt beide GETRENNT,
  // weil es sie verschieden zeigt (Ladezeichen vs. Sanduhr).
  const unbestaetigteProfile = new Set(unbestaetigteStarts.keys())
  const gesperrteStarts = new Set([...startendeProfile, ...unbestaetigteProfile])

  // 30-Tage-Window fuer Aggregate
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60_000
  const last30dRuns = runs.filter((r) => new Date(r.startedAt).getTime() >= thirtyDaysAgo)
  const errorCount = last30dRuns.filter((r) => r.status === 'failed').length
  const warnCount = last30dRuns.filter(
    (r) => r.status === 'completed' && r.errors && r.errors.length > 0,
  ).length
  const healthyProfiles = profiles.filter((p) => profileStatus(p).tone === 'good').length

  // History-Rows fuer Tabelle
  const allHistoryRows: SyncHistoryRow[] = runs.map((run) => {
    const profile = profiles.find((p) => p.id === run.profileId)
    return {
      id: run.id,
      startedAt: formatDateTime(run.startedAt),
      profileLabel: profile ? profile.name : `Profile #${run.profileId}`,
      syncType: SYNC_TYPE_LABEL[run.syncType] ?? run.syncType,
      duration: formatDuration(run.startedAt, run.completedAt),
      entitiesSynced: run.entitiesSynced ?? 0,
      status: run.status,
      hasErrors: !!(run.errors && run.errors.length > 0),
      errorMessage: run.errors && run.errors.length > 0 ? run.errors.join(' · ') : null,
    }
  })

  const visibleHistoryRows = allHistoryRows.filter((r) => {
    if (filter === 'all') return true
    if (filter === 'error') return r.status === 'failed'
    if (filter === 'warn') return r.hasErrors && r.status !== 'failed'
    return true
  })

  async function handleToggleActive(profileId: number, next: boolean) {
    setMeldung(null)
    const queryKey = ['lwa-profiles']
    const previousIsActive = queryClient
      .getQueryData<Profile[]>(queryKey)
      ?.find((p) => p.id === profileId)?.isActive
    // Optimistic Update
    queryClient.setQueryData<Profile[]>(queryKey, (prev) =>
      (prev ?? []).map((p) => (p.id === profileId ? { ...p, isActive: next ? 1 : 0 } : p)),
    )
    setTogglingId(profileId)
    try {
      await api.put(`/lwa/profiles/${profileId}/active/`, { isActive: next })
      queryClient.invalidateQueries({ queryKey })
    } catch (err) {
      // Rollback bei Fehler: nur das betroffene Profil zuruecksetzen, damit
      // zwischenzeitlich erfolgreiche Updates anderer Profile erhalten bleiben
      // (kein Whole-Cache-Overwrite).
      queryClient.setQueryData<Profile[]>(queryKey, (prev) =>
        prev && previousIsActive !== undefined
          ? prev.map((p) => (p.id === profileId ? { ...p, isActive: previousIsActive } : p))
          : prev,
      )
      setMeldung(
        `Umschalten fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`,
      )
    } finally {
      setTogglingId(null)
    }
  }

  async function startSyncForProfile(profileId: number, typ: SyncTyp = SYNC_TYP_DEFAULT) {
    const generation = lebenszyklusGeneration.current
    if (!generationIstAktiv(generation)) return
    setMeldung(null)
    merkeStart(profileId, generation)
    try {
      // Der gewaehlte Typ geht MIT. Bis hierher schickte die Seite immer
      // 'full' — und nach GLB-8 zaehlt ein full-Lauf nicht als Nachweis fuer
      // einen einzelnen Typ. Ein Sync-Zustands-Block war ueber die Oberflaeche
      // deshalb gar nicht aufloesbar: die Sperre war sichtbar, das Mittel
      // dagegen nicht.
      const anfrage = api.post<{ syncRunId?: number }>('/sync/start/', { type: typ, profileId })
      // Die Anfrage wird FESTGEHALTEN, nicht nur abgewartet: Auch wenn die
      // Frist unten zuerst zurueckkommt, zaehlt die Schwelle erst ab ihrer
      // wirklichen Antwort (siebte Runde).
      schwelleNachAntwort(profileId, anfrage, generation)
      const antwort = await mitLebenszyklusFrist(anfrage, START_FRIST_MS, generation)
      if (antwort === LEBENSZYKLUS_ENDE || !generationIstAktiv(generation)) return
      // AB HIER EXISTIERT MOEGLICHERWEISE EIN LAUF. Die Sperre wandert in die zweite Stufe,
      // bevor irgendetwas anderes schiefgehen kann: Ein Nachladen, das
      // scheitert, darf die Zeile nicht freigeben — und eines, das nie
      // antwortet, darf sie nicht fuer immer halten.
      merkeUnbestaetigt(profileId, generation, antwort === OHNE_ANTWORT)
      vergissStart(profileId, generation)
      // OHNE ANTWORT WEISS DIE SEITE NICHTS (R1-Befund, sechste Runde) — auch
      // nicht, dass nichts passiert ist. Der Lauf kann angelegt sein. Also
      // sagen statt schweigen; die Zeile bleibt ueber die zweite Stufe zu, bis
      // die Historie es klaert.
      if (antwort === OHNE_ANTWORT) {
        throw new Error(
          `Keine Antwort binnen ${START_FRIST_MS / 1000} Sekunden — der Lauf kann trotzdem angelegt sein`,
        )
      }
      // ERST NACHLADEN, DANN ENTSPERREN (R2-Befund). Vorher wurde die Zeile
      // freigegeben, sobald die Antwort da war, und die Historie erst eine
      // Sekunde spaeter geholt — dazwischen war die Zeile bedienbar, obwohl
      // der Lauf gerade erst begonnen hatte. Wer in diese Luecke klickt,
      // startet denselben Sync ein zweites Mal.
      if (!(await warteImLebenszyklus(SCHREIB_VORLAUF_MS, generation))) return
      await loadRuns()
    } catch (err) {
      if (!generationIstAktiv(generation)) return
      // ALS VERGANGENES EREIGNIS formuliert (R1+R2, zweite Runde). Der
      // Servertext ist eine Behauptung ueber JETZT — "Ein Sync laeuft bereits".
      // Seit die Meldung stehen bleibt, bis der Bediener sie wegklickt, wuerde
      // dieser Rohtext der Tabelle daneben spaeter widersprechen: dort ist der
      // Lauf laengst beendet, das Banner behauptet weiter, es laufe einer. Der
      // Rahmen macht daraus, was es ist — der Grund, aus dem DIESER Start nicht
      // durchging. Dieselbe Klasse steckte im Umschalter (dort ebenfalls
      // gerahmt); der Sammellauf war schon so formuliert.
      setMeldung(
        `Start fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`,
      )
      // DIE KLAERUNG ANFORDERN (neunte Runde): Die zweite Sperrstufe faellt
      // erst mit einer Liste, die NACH dem Ende der Anfrage losging — eine
      // Ablehnung allein beweist ja nicht, dass kein Lauf entstand. Ohne
      // diesen Abruf bliebe die Zeile bis zum naechsten Takt zu, also bis zu
      // fuenf Sekunden nach einer Absage, die der Bediener schon liest.
      // MIT DEMSELBEN VORLAUF WIE DER ERFOLGSWEG (elfte Runde): Sofort zu
      // fragen liest womoeglich, bevor das Backend den Lauf geschrieben hat —
      // und eine Liste ohne ihn gibt die Zeile frei, waehrend er anlaeuft.
      planeZeitgeber(() => {
        void loadRuns()
      }, SCHREIB_VORLAUF_MS, generation)
    } finally {
      vergissStart(profileId, generation)
    }
  }

  // Wartet bis ein Sync-Run nicht mehr 'running' ist. Backend hat einen
  // impliziten Worker-Mutex — paralleler Trigger fuehrt zu Zombie-Runs.
  async function waitForRunDone(
    runId: number,
    generation: number,
    maxSec = 600,
  ): Promise<boolean> {
    const startMs = Date.now()
    // HOECHSTENS EINE OFFENE ABFRAGE (R1+R2, siebte Runde): Die Frist beendet
    // das Warten, damit die Obergrenze unten ueberhaupt wieder zum Vergleich
    // kommt. Ohne diesen Merker schickte die Schleife alle 15 Sekunden eine
    // weitere hinterher und stapelte sie ueber die ganzen zehn Minuten auf.
    //
    // VERFALL = ABBRUCH (Scheibe SYNC-ABBRUCH-UND-STARTZEICHEN): Nach
    // `PLATZ_VERFALL_MS` wird die Abfrage nicht bloss aufgegeben, sondern
    // beendet. Eine spaete Antwort NACH dem Verfall gibt es damit nicht mehr —
    // die naechste Abfrage fragt frisch, und deren Antwort zaehlt. Zwischen
    // Frist (15 s) und Verfall (60 s) wartet jede Runde weiter auf DIESELBE
    // offene Abfrage.
    let offen: Promise<{ run: SyncRun }> | null = null
    // ENDE IM SCHLAF-FENSTER (R1 zur Abbruch-Scheibe, Entscheid E1: „zwischen
    // 15 s und 60 s zaehlt eine spaete Antwort weiterhin"): Es gibt ein
    // Fenster, in dem diese Schleife gar nicht zuhoert — den 5-Sekunden-Schlaf
    // nach einer abgelaufenen Frist. Antwortet eine Abfrage dort, hat
    // `freigeben` den Platz schon geraeumt, und die naechste Runde findet ihn
    // frei und fragt NEU: der gesehene Abschluss waere verloren, und haengen
    // die Folge-Abfragen, meldete der Sammellauf nach zehn Minuten „nicht
    // bestaetigt", obwohl das Ende laengst bekannt war. Jede Abfrage meldet
    // ihren Abschluss deshalb hierher.
    //
    // Der Merker ist ENGER als die Sammelstelle aus PR #56 (elfte Runde): Die
    // fing auch noch die Antwort NACH dem Verfall auf — die gibt es seit
    // VERFALL = ABBRUCH nicht mehr. Uebrig bleibt allein das Schlaf-Fenster
    // zwischen Frist und Verfall.
    let endeGesehen = false
    while (generationIstAktiv(generation) && Date.now() - startMs < maxSec * 1000) {
      if (!(await warteImLebenszyklus(5000, generation))) return false
      if (endeGesehen) return true
      try {
        if (!offen) {
          const steuerung = new AbortController()
          if (!registriereLeseSteuerung(steuerung, generation)) return false
          const abfrage = api.get<{ run: SyncRun }>(`/sync/runs/${runId}/`, { signal: steuerung.signal })
          offen = abfrage
          // NUR DER EIGENE PLATZ (R1, elfte Runde): Der Verfall-Timer einer
          // frueh beantworteten Abfrage faellt, wenn laengst der Nachfolger
          // den Platz haelt.
          const freigeben = () => {
            if (offen === abfrage) offen = null
          }
          void abfrage.then(
            (daten) => {
              if (
                generationIstAktiv(generation)
                && daten?.run
                && daten.run.status !== 'running'
              ) endeGesehen = true
            },
            () => null,
          )
          void abfrage.catch(() => null).then(() => {
            vergissLeseSteuerung(steuerung)
            freigeben()
          })
          planeZeitgeber(() => {
            steuerung.abort()
            vergissLeseSteuerung(steuerung)
            freigeben()
          }, PLATZ_VERFALL_MS, generation)
        }
        // MIT FRIST (R1-Befund, fuenfte Runde): Ohne sie haelt ein einzelner
        // Abruf, der nie antwortet, diese Schleife trotz ihrer Obergrenze
        // unbegrenzt auf — und mit ihr den ganzen Sammellauf.
        const data = await mitLebenszyklusFrist(offen, ABRUF_FRIST_MS, generation)
        if (data === LEBENSZYKLUS_ENDE) return false
        if (data !== OHNE_ANTWORT && data.run && data.run.status !== 'running') return true
      } catch {
        // Polling-Fehler — auch der eigene Abbruch — ignorieren, weiter versuchen
      }
    }
    // OBERGRENZE ERREICHT, ENDE NICHT GESEHEN (R1-Befund, sechste Runde).
    // Frueher war das von einem bestaetigten Abschluss nicht zu unterscheiden —
    // der Sammellauf startete danach das naechste Profil, obwohl der vorige
    // Lauf noch laufen konnte. Genau davor warnt der Hinweis oben.
    return false
  }

  async function startSyncForAll(typ: SyncTyp = SYNC_TYP_DEFAULT) {
    const generation = lebenszyklusGeneration.current
    if (profiles.length === 0 || !generationIstAktiv(generation)) return
    setMeldung(null)
    setBulkSyncing(true)
    /**
     * ALLE Ausfaelle dieses Laufs, nicht nur der letzte (R1+R2, zweite Runde).
     *
     * Vorher schrieb jeder gescheiterte Start denselben einen Platz neu: fielen
     * zwei Profile aus, war am Ende nur noch das zuletzt gescheiterte benannt.
     * Das erste verschwand, ohne dass der Bediener es je gesehen haben musste —
     * und die Historie faengt das nicht auf: eine abgewiesene Startanfrage legt
     * gar keinen Lauf an, ueber den man sie spaeter noch finden koennte.
     */
    const ausfaelle: Array<{ name: string; grund: string }> = []
    try {
      const active = profiles.filter((p) => p.isActive)
      for (const p of active) {
        if (!generationIstAktiv(generation)) return
        merkeStart(p.id, generation)
        // Ob die Schleife nach diesem Profil weitergehen darf. Sie darf es
        // nicht, wenn ueber den Ausgang des Laufs nichts bekannt ist.
        let weiter = true
        try {
          const anfrage = api.post<{ syncRunId: number; status: string }>(
            '/sync/start/',
            // EIN Typ fuer den ganzen Sammellauf (Entscheid des
            // Koordinators): ein Lauf, der je Zeile etwas anderes tut, waere
            // in der Historie nicht mehr lesbar.
            { type: typ, profileId: p.id },
          )
          // Auch hier zaehlt die Schwelle ab der wirklichen Antwort, nicht ab
          // dem Ende des Wartens (siebte Runde).
          schwelleNachAntwort(p.id, anfrage, generation)
          const result = await mitLebenszyklusFrist(anfrage, START_FRIST_MS, generation)
          if (result === LEBENSZYKLUS_ENDE || !generationIstAktiv(generation)) return
          // Auch hier ist der Lauf ab der Antwort in der Welt — und auch hier
          // kann das Nachladen darunter ausbleiben. Ohne `runId` gibt es sogar
          // gar nichts, worauf man warten koennte.
          merkeUnbestaetigt(p.id, generation, result === OHNE_ANTWORT)
          if (result === OHNE_ANTWORT) {
            weiter = false
            throw new Error(
              `Keine Antwort binnen ${START_FRIST_MS / 1000} Sekunden — der Lauf kann trotzdem angelegt sein`,
            )
          }
          const runId = result?.syncRunId
          if (runId) {
            const bestaetigt = await waitForRunDone(runId, generation)
            if (!generationIstAktiv(generation)) return
            const nachladen = await mitLebenszyklusFrist(loadRuns(), ABRUF_FRIST_MS, generation)
            if (nachladen === LEBENSZYKLUS_ENDE || !generationIstAktiv(generation)) return
            if (!bestaetigt) {
              weiter = false
              throw new Error(
                'Das Ende des Laufs wurde nicht bestaetigt — der Sammellauf bricht ab, ' +
                  'statt den naechsten Start darauf zu setzen',
              )
            }
          }
        } catch (err) {
          if (!generationIstAktiv(generation)) return
          // NUR EINE EMPFANGENE HTTP-ANTWORT BEWEIST, DASS DER SERVER MIT
          // DIESER ANFRAGE FERTIG IST (R1-Befund, elfte Runde). Der Client
          // wartet nur auf `fetch`; lehnt der ab, kann der POST laengst
          // uebertragen und `startSync` auf dem Server noch am Arbeiten sein.
          // Den naechsten Start darauf zu setzen ist genau der parallele
          // Trigger, vor dem der Hinweis an `waitForRunDone` warnt — und es
          // widerspricht der Zusicherung ein paar Zeilen weiter unten.
          // `ApiError` entsteht ausschliesslich aus einer gelesenen Antwort
          // (client.ts); eine fachliche Absage ist also ein sicher beendeter
          // Start, ein Transportfehler nicht.
          const schonAbgebrochen = !weiter
          const serverHatGeantwortet = err instanceof ApiError
          if (!serverHatGeantwortet) weiter = false
          const grund = err instanceof Error ? err.message : 'Unbekannter Fehler'
          ausfaelle.push({
            name: p.name,
            grund:
              serverHatGeantwortet || schonAbgebrochen
                ? grund
                : `${grund} — der Sammellauf bricht ab, statt den naechsten ` +
                  'Start darauf zu setzen',
          })
          // Nach JEDEM Ausfall neu gesetzt, nicht erst am Ende: der Sammellauf
          // wartet je Profil auf dessen Ende und laeuft dadurch lange. Wer
          // solange auf eine leere Seite sieht, haelt den Lauf fuer gesund.
          setMeldung(
            ausfaelle.length === 1
              ? `Sync fuer ${ausfaelle[0]!.name} fehlgeschlagen: ${ausfaelle[0]!.grund}`
              : `Sync fuer ${ausfaelle.length} Profile fehlgeschlagen — ` +
                ausfaelle.map((a) => `${a.name}: ${a.grund}`).join('; '),
          )
          // Auch hier die Klaerung anfordern (neunte Runde), aus demselben
          // Grund wie im Einzelstart — und mit demselben Vorlauf (elfte).
          planeZeitgeber(() => {
            void loadRuns()
          }, SCHREIB_VORLAUF_MS, generation)
        } finally {
          // Je Zeile wieder heraus, sobald SIE dran war — nicht erst am Ende
          // des ganzen Sammellaufs.
          vergissStart(p.id, generation)
        }
        // ABBRUCH NACH DEM AUFRAEUMEN (R1-Befund, sechste Runde): Das Backend
        // hat einen impliziten Worker-Mutex. Weiterzumachen, ohne das Ende des
        // vorigen Laufs gesehen zu haben, ist genau der parallele Trigger, vor
        // dem der Hinweis an `waitForRunDone` warnt.
        if (!generationIstAktiv(generation)) return
        if (!weiter) break
      }
    } finally {
      if (!generationIstAktiv(generation)) return
      setBulkSyncing(false)
      // MIT DEMSELBEN VORLAUF (elfte Runde, selbst gemessen): Dieser Nachlauf
      // holte die Historie nach 500 ms und damit VOR dem Vorlauf, den der
      // Fehlerweg dem Backend gerade laesst. Die zu fruehe Liste entschied
      // dann doch wieder — der Vorlauf war im Sammellauf wirkungslos.
      planeZeitgeber(() => {
        void loadRuns()
      }, SCHREIB_VORLAUF_MS, generation)
    }
  }

  const cronTone: BadgeTone = IS_DEMO_MODE ? 'warn' : 'good'
  const cronLabel = IS_DEMO_MODE ? 'Demo-Modus' : 'Aktiv'
  const cronSubtitle = IS_DEMO_MODE ? 'keine Auto-Optimierung' : '08:00 täglich'

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">
            Sync-Center
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Manueller Sync je Profil · Run-Historie · Cron-Status.
          </p>
        </div>
        {/*
          NICHT MEHR GESPERRT, waehrend geladen wird (R1-Befund, vierte Runde).
          `refreshing` geht erst zurueck, wenn der JUENGSTE Abruf antwortet.
          Antwortet er nie — und es gibt keinen laufenden Lauf, also auch keinen
          Takt, der ihn ueberholen koennte —, war dieser Knopf dauerhaft
          gesperrt: ausgerechnet der einzige Weg, den das rote Banner daneben
          anbietet. Ueberlappende Abrufe sind seit der Nummernvergabe harmlos:
          Eine spaete Antwort schreibt nicht mehr ueber eine neuere. Das
          Ladezeichen sagt weiterhin, dass etwas laeuft — es entmuendigt nur
          niemanden mehr.
        */}
        <button
          type="button"
          onClick={() => void loadRuns()}
          aria-busy={refreshing}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm hover:bg-[var(--accent)]"
        >
          <RefreshCcw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
          Aktualisieren
        </button>
      </div>

      {meldung && (
        <div className="flex items-start justify-between gap-4 rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{meldung}</span>
          <button onClick={() => setMeldung(null)} className="text-xs underline">
            ausblenden
          </button>
        </div>
      )}

      {/*
        OHNE Schliessen-Knopf, und das mit Absicht: Dieser Hinweis beschreibt
        keinen Vorfall, sondern den Zustand von jetzt. Wegklicken wuerde ihn
        nicht loesen, sondern nur verbergen — und der naechste gelungene Abruf
        nimmt ihn ohnehin von selbst zurueck. Der Weg dorthin steht daneben:
        „Aktualisieren".
      */}
      {/*
        `role="alert"` (R1+R2 zur Abbruch-Scheibe): Die Zusicherung „ein eigener
        Abbruch ist kein Ladefehler" liess sich vorher nur ueber den WORTLAUT
        des Hinweises pruefen — und die Seite traegt an anderer Stelle die feste
        Aufschrift „Sync abgebrochen · Re-Auth noetig", die jede naheliegende
        Textsuche traf. Ueber die Rolle ist der Hinweis eindeutig auffindbar,
        unabhaengig davon, welchen Text ein Fehler gerade mitbringt. Fuer
        Vorleseprogramme ist es ohnehin die richtige Auszeichnung.
      */}
      {ladeFehler && (
        <div
          role="alert"
          className="flex items-start gap-4 rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span>{ladeFehler}</span>
        </div>
      )}

      {/* Status-Cards */}
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Card className="p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
            Profile gesund
          </div>
          <div className="mt-1 text-[22px] font-bold tabular-nums text-emerald-600">
            {healthyProfiles} <span className="text-[var(--muted-foreground)]">/ {profiles.length || '–'}</span>
          </div>
          <div className="mt-0.5 text-[11.5px] text-[var(--muted-foreground)]">aktiv · synchronisierend</div>
        </Card>
        <Card
          className={cn(
            'p-4',
            warnCount > 0 && 'border-l-[3px] border-l-amber-500',
          )}
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
            Warnungen (30 T)
          </div>
          <div
            className={cn(
              'mt-1 text-[22px] font-bold tabular-nums',
              warnCount > 0 ? 'text-amber-600' : 'text-[var(--foreground)]',
            )}
          >
            {warnCount}
          </div>
          <div className="mt-0.5 text-[11.5px] text-[var(--muted-foreground)]">
            API-Teilausfälle, Daten-Lücken
          </div>
        </Card>
        <Card
          className={cn('p-4', errorCount > 0 && 'border-l-[3px] border-l-red-500')}
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
            Fehler (30 T)
          </div>
          <div
            className={cn(
              'mt-1 text-[22px] font-bold tabular-nums',
              errorCount > 0 ? 'text-red-600' : 'text-[var(--foreground)]',
            )}
          >
            {errorCount}
          </div>
          <div className="mt-0.5 text-[11.5px] text-[var(--muted-foreground)]">
            Sync abgebrochen · Re-Auth nötig
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
            Cron-Status
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[14px] font-bold text-[var(--foreground)]">
            <Dot tone={cronTone} /> {cronLabel}
          </div>
          <div className="mt-0.5 text-[11.5px] text-[var(--muted-foreground)]">{cronSubtitle}</div>
        </Card>
      </div>

      {/* Profile-Grid */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between gap-4 border-b border-[var(--border)] px-4 py-3">
          <SectionHead
            title="Profile-Übersicht"
            subtitle={`${profiles.length} LWA-autorisierte Märkte · einzeln triggerbar`}
            className="mb-0"
          />
          <label htmlFor="sync-typ-alle" className="text-xs font-medium text-[var(--muted-foreground)]">
            Typ
          </label>
          <SyncTypeSelect
            id="sync-typ-alle"
            label="Sync-Typ für alle Profile"
            wert={bulkTyp}
            onChange={setBulkTyp}
            disabled={bulkSyncing}
          />
          <button
            type="button"
            onClick={() => startSyncForAll(bulkTyp)}
            disabled={
              bulkSyncing ||
              profiles.length === 0 ||
              laufendeProfile.size > 0 ||
              // AUCH EINE LAUFENDE START-ANFRAGE sperrt (R1-Befund): Sonst
              // koennte ein Sammellauf dasselbe Profil ein zweites Mal
              // anstossen, waehrend dessen Einzel-Anfrage noch offen ist —
              // und das erste beendete `finally` naehme die gemeinsame Sperre
              // weg, obwohl die andere Anfrage noch laeuft.
              gesperrteStarts.size > 0
            }
            title={
              laufendeProfile.size > 0 || gesperrteStarts.size > 0
                ? 'Es laeuft bereits ein Sync — Sammellauf waehrenddessen gesperrt'
                : 'Startet alle aktiven Profile sequenziell — kann mehrere Minuten dauern'
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {bulkSyncing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCcw className="h-3 w-3" />
            )}
            Alle synchronisieren
          </button>
        </div>
        <ProfileSyncGrid
          profiles={profileEntries}
          startendeProfile={startendeProfile}
          unbestaetigteProfile={unbestaetigteProfile}
          togglingId={togglingId}
          onSync={startSyncForProfile}
          syncTypen={syncTypen}
          onSyncTypChange={(profileId, typ) => setSyncTypen((prev) => ({ ...prev, [profileId]: typ }))}
          bulkSyncing={bulkSyncing}
          laufendeProfile={laufendeProfile}
          onToggleActive={handleToggleActive}
        />
      </Card>

      {/* Run-Historie */}
      <Card className="overflow-hidden p-0">
        <div className="border-b border-[var(--border)] px-4 py-3">
          <SectionHead
            title="Run-Historie"
            subtitle={`${runs.length} Sync-Läufe gespeichert`}
            className="mb-0"
          />
        </div>
        <SyncRunHistoryTable
          rows={visibleHistoryRows}
          filter={filter}
          onFilterChange={setFilter}
          totalCount={allHistoryRows.length}
          warnCount={allHistoryRows.filter((r) => r.hasErrors && r.status !== 'failed').length}
          errorCount={allHistoryRows.filter((r) => r.status === 'failed').length}
        />
      </Card>
    </div>
  )
}
